import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CheckCircle2, ExternalLink, Loader2, Package, Search, Truck } from "lucide-react";
import { API, EUR } from "@/lib/api";

const FULFILLMENT_LABELS = {
    pending: "En attente de paiement",
    queued: "Confirmée — en préparation",
    ordered: "Commandée chez le fournisseur",
    shipped: "Expédiée — en cours de livraison",
    cancelled: "Annulée",
};

export default function TrackOrder() {
    const [params] = useSearchParams();
    const [ref, setRef] = useState(params.get("ref") || "");
    const [email, setEmail] = useState("");
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        setResult(null);
        try {
            const r = await fetch(
                `${API}/orders/track?ref=${encodeURIComponent(ref.trim())}&email=${encodeURIComponent(email.trim())}`
            );
            if (!r.ok) throw new Error("not found");
            setResult(await r.json());
        } catch (err) {
            setError("Commande introuvable. Vérifiez votre référence et votre e-mail.");
        }
        setLoading(false);
    };

    const steps = result
        ? [
              { label: "Paiement confirmé", done: result.paid },
              { label: "Préparation & commande fournisseur", done: result.paid },
              { label: "Expédiée — livraison suivie", done: result.fulfillment_status === "shipped", note: result.tracking_number },
          ]
        : null;

    return (
        <div className="min-h-screen bg-cream text-ink font-sans" data-testid="track-page">
            <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10 lg:py-16">
                <Link to="/" data-testid="track-back-link" className="inline-flex items-center gap-2 text-sm font-medium text-sage hover:text-sage-hover transition-colors">
                    <ArrowLeft size={16} /> Retour à la boutique
                </Link>
                <h1 className="mt-6 font-serif text-3xl sm:text-4xl tracking-tight">Suivre ma commande</h1>
                <p className="mt-2 text-sm text-[#4A5550]">
                    La référence de commande (elle commence par « cs_ ») figure dans votre e-mail de confirmation.
                </p>

                <form data-testid="track-form" onSubmit={submit} className="mt-8 rounded-3xl bg-white border border-sage/15 p-6 sm:p-8 flex flex-col gap-4">
                    <div>
                        <label className="text-xs font-semibold text-ink/70 mb-1.5 block">Référence de commande</label>
                        <input
                            data-testid="track-field-ref"
                            required
                            value={ref}
                            onChange={(e) => setRef(e.target.value)}
                            placeholder="cs_xxxxxxxx…"
                            className="w-full rounded-xl border border-sage/20 bg-cream px-4 py-3 text-sm outline-none focus:border-sage focus:ring-2 focus:ring-sage/20"
                        />
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-ink/70 mb-1.5 block">E-mail utilisé lors de la commande</label>
                        <input
                            data-testid="track-field-email"
                            required
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="vous@exemple.fr"
                            className="w-full rounded-xl border border-sage/20 bg-cream px-4 py-3 text-sm outline-none focus:border-sage focus:ring-2 focus:ring-sage/20"
                        />
                    </div>
                    <button
                        data-testid="track-submit-button"
                        type="submit"
                        disabled={loading}
                        className="mt-1 w-full rounded-full bg-sage hover:bg-sage-hover disabled:opacity-60 text-cream font-semibold py-3.5 flex items-center justify-center gap-2 transition-all hover:scale-[1.01]"
                    >
                        {loading ? <Loader2 size={17} className="animate-spin" /> : <Search size={16} />}
                        {loading ? "Recherche…" : "Suivre ma commande"}
                    </button>
                    {error && (
                        <p data-testid="track-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                            {error}
                        </p>
                    )}
                </form>

                {result && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        data-testid="track-result"
                        className="mt-6 rounded-3xl bg-white border border-sage/15 p-6 sm:p-8"
                    >
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <div>
                                <p className="font-semibold">{result.pack_label}</p>
                                <p className="text-sm text-[#4A5550]">{EUR(result.amount)} — {result.reference.slice(0, 18)}…</p>
                            </div>
                            <span
                                data-testid="track-status-badge"
                                className="rounded-full bg-sage-soft text-sage px-3.5 py-1.5 text-xs font-semibold"
                            >
                                {FULFILLMENT_LABELS[result.fulfillment_status] || FULFILLMENT_LABELS.queued}
                            </span>
                        </div>
                        <ol className="mt-6 flex flex-col gap-0" data-testid="track-timeline">
                            {steps.map((s, i) => (
                                <li key={i} className="flex gap-3">
                                    <div className="flex flex-col items-center">
                                        <span className={`w-7 h-7 rounded-full grid place-items-center shrink-0 ${s.done ? "bg-sage text-cream" : "bg-sand/70 text-ink/40"}`}>
                                            {i === 2 && s.done ? <Truck size={13} /> : <CheckCircle2 size={14} />}
                                        </span>
                                        {i < steps.length - 1 && <span className={`w-0.5 flex-1 min-h-[28px] ${s.done ? "bg-sage" : "bg-sand"}`} />}
                                    </div>
                                    <div className="pb-6">
                                        <p className={`text-sm font-medium ${s.done ? "text-ink" : "text-ink/40"}`}>{s.label}</p>
                                        {s.note && (
                                            <p className="mt-1 text-xs font-mono text-sage-mid" data-testid="track-tracking-number">N° de suivi : {s.note}</p>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ol>
                        {result.tracking_number && (
                            <a
                                data-testid="track-17track-button"
                                href={`https://www.17track.net/fr/track?nums=${encodeURIComponent(result.tracking_number)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-2 inline-flex items-center gap-2 rounded-full bg-sage hover:bg-sage-hover text-cream text-sm font-semibold px-6 py-3 transition-all hover:scale-[1.02]"
                            >
                                <ExternalLink size={15} /> Suivre mon colis en direct
                            </a>
                        )}
                    </motion.div>
                )}

                {!result && !error && (
                    <div className="mt-6 rounded-3xl border border-dashed border-sage/25 p-6 text-center" data-testid="track-hint">
                        <Package size={22} className="mx-auto text-sage/40" />
                        <p className="mt-3 text-sm text-[#4A5550]">
                            Astuce : votre référence de commande vous a été envoyée par e-mail dès votre achat.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}
